import React, { useState } from "react";
import Modal from "./Modal.tsx";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import InputField from "../Forms/InputField.js";
import SelectField from "../Forms/SelectField.js";
import { updateStaff } from "../../services/users.js";
import toast from "react-hot-toast";
import { dateDefaultValue, dateFormat } from "../../utils/functions.js";

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  gender: Yup.string().required("Gender is required"),
  dateOfBirth: Yup.date().required("Date of birth is required"),
  ethnicity: Yup.string().required("Ethnicity is required"),
  startDate: Yup.date().required("Start date is required"),
  address: Yup.string().required("Address is required"),
  postalCode: Yup.string().required("Postcode is required"),
  telephoneNumber: Yup.string().required("Phone number is required"),
});

const CreateStaff = ({ isModalOpen, setIsModalOpen, reloadUsers, user }) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...user,
      dateOfBirth: dateDefaultValue(user.dateOfBirth),
      startDate: dateDefaultValue(user.startDate),
    },
  });

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Handle form submission
  const onSubmit = async (data) => {
    console.log(data);
    delete data.staffId;
    delete data.userId;
    delete data.documents;
    const payload = {
      ...data,
      dateOfBirth: dateFormat(data.dateOfBirth, "yyyy-MM-dd HH:mm:ss"),
      startDate: dateFormat(data.startDate, "yyyy-MM-dd HH:mm:ss"),
    };
    setIsLoading(true);
    try {
      await updateStaff(payload, user.staffId);
      toast.success("User updated successfully");
      reloadUsers();
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update user");
    } finally {
      setIsLoading(false);
    }
  };

  // Form schema configuration
  const userFormSchema = {
    sections: [
      {
        title: "User Detail",
        fields: [
          {
            label: "First Name",
            key: "firstName",
            type: "text",
            required: true,
          },
          {
            label: "Last Name",
            key: "lastName",
            type: "text",
            required: true,
          },
          {
            label: "Gender",
            key: "gender",
            type: "select",
            options: [
              { label: "Male", value: "male" },
              { label: "Female", value: "female" },
              { label: "Other", value: "other" },
            ],
            required: true,
          },
          {
            label: "Date Of Birth",
            key: "dateOfBirth",
            type: "date",
            required: true,
          },
          {
            label: "Ethnicity",
            key: "ethnicity",
            type: "select",
            options: [
              { label: "Asian", value: "Asian" },
              {
                label: "Black or African American",
                value: "Black or African American",
              },
              { label: "Hispanic or Latino", value: "Hispanic or Latino" },
              { label: "Middle Eastern", value: "Middle Eastern" },
              { label: "White or Caucasian", value: "White or Caucasian" },
              { label: "Pacific Islander", value: "Pacific Islander" },
              {
                label: "Mixed or Multi-ethnic",
                value: "Mixed or Multi-ethnic",
              },
            ],
            required: true,
          },
          {
            label: "Start Date",
            key: "startDate",
            type: "date",
            required: true,
          },
        ],
      },
      {
        title: "Address Details",
        fields: [
          {
            label: "Full Address",
            key: "address",
            type: "text",
            required: true,
          },
          {
            label: "Postcode",
            key: "postalCode",
            type: "text",
            required: true,
          },
        ],
      },
      {
        title: "Contact Details",
        fields: [
          {
            label: "Phone Number",
            key: "telephoneNumber",
            type: "text",
            required: true,
          },
          {
            label: "Email Address",
            key: "emailAddress",
            type: "email",
            required: false,
          },
        ],
      },
      {
        title: "Employment Details",
        fields: [
          {
            label: "Role",
            key: "role",
            type: "text",
            required: false,
          },
          {
            label: "Manager",
            key: "manager",
            type: "text",
            required: false,
          },
          {
            label: "Permissions",
            key: "permission",
            type: "text",
            required: false,
          },
          {
            label: "Login Code",
            key: "loginCode",
            type: "text",
            required: false,
          },
        ],
      },
    ],
  };

  // Render dynamic form fields using the `InputField` component
  const renderField = (sectionKey, field) => {
    const { label, key, type, options, required } = field;

    switch (type) {
      case "text":
      case "email":
      case "date":
        return (
          <InputField
            key={key}
            name={key}
            control={control}
            label={label}
            type={type}
            error={errors[key]?.message}
            required={required}
          />
        );

      case "select":
        return (
          <SelectField
            {...field}
            key={key}
            control={control}
            label={label}
            name={key}
            options={options}
            required={required}
            error={errors[key]?.message}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Modal
        title="Create staff member"
        onCancel={handleCancel}
        isOpen={isModalOpen}
        size="xlg"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {userFormSchema.sections.map((section) => (
            <div key={section.title} className="form__section">
              <h5>{section.title}</h5>
              <div className="form__section-wrap">
                {section.fields.map((field) =>
                  renderField(section.title, field)
                )}
              </div>
            </div>
          ))}
          <button
            type="submit"
            className="btn btn__primary"
            disabled={isLoading}
          >
            {isLoading ? "Loading" : "Submit"}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default CreateStaff;
